import Image from 'next/image';
import type { ReactNode } from 'react';

import { TrackedButton } from '@/components/button';
import { TRACKED_EVENT_POSITION } from '@/constants';
import { cn } from '@lib/utils';

import type { Product } from './products';

// 카드 앞면(제품 소개). 크기·간격·타이포는 반응형(모바일 280×448 → 데스크탑 360×488).
// '검사 장점 보기' 버튼은 onFlip 이 전달됐을 때(모바일)만 노출된다 — 데스크탑은 hover 로 플립.
export function CardFront({
  product,
  onFlip,
  buttonTabIndex = 0,
  viewBenefitsLabel,
  question,
  name,
  description,
}: {
  product: Product;
  // 모바일 '검사 장점 보기' 버튼 → 플립. 데스크탑은 hover 라 미전달.
  onFlip?: () => void;
  // 앞면이 안 보일 때(-1) 버튼을 탭 순서에서 제외
  buttonTabIndex?: number;
  viewBenefitsLabel?: string;
  question: ReactNode;
  name: string;
  description: ReactNode;
}) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-between rounded-[20px] bg-white px-6 py-7 text-left shadow-[0_20px_60px_-20px_rgba(4,4,53,0.18)] md:justify-start md:rounded-[32px] md:p-8">
      <div className="relative h-[245px] w-full shrink-0 overflow-hidden rounded-[18px] md:h-[314px] md:rounded-[20px]">
        <Image
          src={product.illustBg}
          alt=""
          fill
          sizes="(min-width: 768px) 360px, 230px"
          className="object-cover"
        />
        <div className="relative flex h-full flex-col items-center justify-center gap-[22px] px-4 md:gap-[18px]">
          <p
            className="whitespace-nowrap bg-clip-text text-center text-xl font-semibold leading-7 tracking-[-0.336px] text-transparent md:text-2xl md:leading-8 md:tracking-[-0.464px]"
            style={{ backgroundImage: product.questionGradient }}
          >
            {question}
          </p>
          <Image
            src={product.graphic}
            alt=""
            className={cn('object-contain', product.graphicCls)}
          />
        </div>
      </div>

      <div className="flex w-full flex-col gap-1 md:mt-5 md:gap-1.5">
        <p className="text-xl font-semibold leading-7 tracking-[-0.336px] text-[#2B2E33] md:w-fit md:text-2xl md:leading-8 md:tracking-[-0.464px]">
          {name}
        </p>
        <p className="text-sm font-medium leading-[22px] tracking-[-0.128px] text-[#6C7380] md:text-base md:leading-[26px] md:tracking-[-0.176px]">
          {description}
        </p>
      </div>

      {onFlip && (
        <TrackedButton
          type="button"
          label={`solution-finder_${product.key}_view-benefits`}
          position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
          onClick={onFlip}
          tabIndex={buttonTabIndex}
          className="inline-flex w-full items-center justify-center gap-1 rounded-[40px] border border-solid border-[#2B2E33] py-2.5 text-xs font-semibold leading-[18px] tracking-[-0.14px] text-[#2B2E33]"
        >
          {viewBenefitsLabel}
          <span aria-hidden="true">→</span>
        </TrackedButton>
      )}
    </div>
  );
}
